import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { REPS_COLORS } from '../utils/constants';
import { normalizeThresholds } from '../utils/bands';
import { SCORE_COLORS } from '../utils/scoring';
import { exportTrendCSV } from '../utils/csvExport';

const avg4 = (o) => Math.round((((o.reliability || 0) + (o.efficiency || 0) + (o.productivity || 0) + (o.safety || 0)) / 4) * 100) / 100;

const PERIOD_DAYS = { '30d': 30, '90d': 90, '12m': 365, all: null };
const PERIODS = [
  { value: '30d', label: '30 d' },
  { value: '90d', label: '90 d' },
  { value: '12m', label: '12 m' },
  { value: 'all', label: 'All' },
];
const snapMillis = (snap) => (snap.calculatedAt?.toMillis ? snap.calculatedAt.toMillis() : snap.calculatedAt?.seconds ? snap.calculatedAt.seconds * 1000 : Date.parse(snap.calculatedAt) || 0);

/**
 * @param {string} [period]  '30d' | '90d' | '12m' | 'all' — default 90 d (P3-07); a widget instance
 *                           can pin its own period through its settings (P3-18)
 */
function TrendChart({ snapshots, thresholds, organization, chartHeight = 320, period: periodProp = '90d' }) {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [period, setPeriod] = useState(periodProp);
  useEffect(() => setPeriod(periodProp), [periodProp]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleExportCSV = () => {
    try {
      const options = {};
      if (startDate) options.startDate = startDate;
      if (endDate) options.endDate = endDate;
      exportTrendCSV(snapshots, organization, options);
    } catch (err) {
      console.error('Trend CSV export error:', err);
    }
  };

  if (!snapshots || snapshots.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Organizational Entropy (OE) Trends
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No trend data available yet. Complete surveys to see trends over time.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Period + custom range filter the chart (and the CSV export shares the same dates).
  const days = PERIOD_DAYS[period] ?? 90;
  const since = days ? Date.now() - days * 86400000 : 0;
  const startMs = startDate ? Date.parse(startDate) : null;
  const endMs = endDate ? Date.parse(endDate) + 86400000 : null;
  const visible = snapshots.filter((snap) => {
    const t = snapMillis(snap);
    if (startMs || endMs) return (!startMs || t >= startMs) && (!endMs || t < endMs);
    return t >= since;
  });

  // Transform snapshots into chart data (oldest first for time series)
  const chartData = [...(visible.length ? visible : snapshots)]
    .reverse()
    .map((snap) => {
      const period = snap.period || '';
      const calculatedAt = snap.calculatedAt?.toDate
        ? snap.calculatedAt.toDate().toLocaleDateString()
        : period;
      return {
        period: period || calculatedAt,
        Reliability: snap.oeScores?.reliability ?? null,
        Efficiency: snap.oeScores?.efficiency ?? null,
        Productivity: snap.oeScores?.productivity ?? null,
        Safety: snap.oeScores?.safety ?? null,
        // P2-35: the same snapshot split into what people report and what approved conditions add
        Perception: snap.perceptionScores ? avg4(snap.perceptionScores) : null,
        Reality: snap.conditionScores ? avg4(snap.conditionScores) : null,
      };
    });
  const hasSplit = chartData.some((d) => d.Perception != null);

  // Average threshold for reference line
  const tN = normalizeThresholds(thresholds);
  const avgThreshold = (tN.reliability + tN.efficiency + tN.productivity + tN.safety) / 4;
  const zoneLo = Math.max(0, avgThreshold - 2);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">
            Organizational Entropy (OE) Trends
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={startDate || endDate ? null : period} onChange={(_, v) => { if (v) { setPeriod(v); setStartDate(''); setEndDate(''); } }} aria-label="Trend period">
              {PERIODS.map((p) => (
                <ToggleButton key={p.value} value={p.value} sx={{ px: 1, py: 0.25, textTransform: 'none' }}>{p.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button
              size="small"
              variant="text"
              startIcon={<FilterListIcon />}
              onClick={() => setShowDateFilter(!showDateFilter)}
            >
              {showDateFilter ? 'Hide Filters' : 'Date Range'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
          </Stack>
        </Box>

        {/* Date range filter */}
        <Collapse in={showDateFilter}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
            {(startDate || endDate) && (
              <Button
                size="small"
                onClick={() => { setStartDate(''); setEndDate(''); }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Collapse>

        <Box sx={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              {/* Color-coded score zones follow the org's (average) threshold T: green < T−2, yellow T−2..T, red > T (P5-01) */}
              <ReferenceArea y1={0} y2={zoneLo} fill={SCORE_COLORS.green} fillOpacity={0.06} />
              <ReferenceArea y1={zoneLo} y2={avgThreshold} fill={SCORE_COLORS.yellow} fillOpacity={0.06} />
              <ReferenceArea y1={avgThreshold} y2={10} fill={SCORE_COLORS.red} fillOpacity={0.06} />
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 10]}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'OE Score',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  fontSize: 13,
                }}
                formatter={(value) => value != null ? value.toFixed(1) : 'N/A'}
              />
              <Legend />
              <ReferenceLine
                y={avgThreshold}
                stroke="#c62828"
                strokeDasharray="5 5"
                label={{
                  value: `Threshold (${avgThreshold.toFixed(0)})`,
                  position: 'right',
                  fill: '#c62828',
                  fontSize: 11,
                }}
              />
              <Line
                type="monotone"
                dataKey="Reliability"
                stroke={REPS_COLORS.reliability}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="Efficiency"
                stroke={REPS_COLORS.efficiency}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="Productivity"
                stroke={REPS_COLORS.productivity}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="Safety"
                stroke={REPS_COLORS.safety}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
              {hasSplit && (
                <Line type="monotone" dataKey="Perception" name="Surveys (perception, reference)" stroke="#546e7a" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
              )}
              {hasSplit && (
                <Line type="monotone" dataKey="Reality" name="Approved OE Conditions" stroke="#8e24aa" strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TrendChart;
